import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Trash2, Download, Copy, Image as ImageIcon } from "lucide-react";

const FORMATS = [
  { key: "square", label: "instagram · 1:1", w: 1080, h: 1080 },
  { key: "portrait", label: "feed · 4:5", w: 1080, h: 1350 },
  { key: "story", label: "story · 9:16", w: 1080, h: 1920 },
];

/**
 * Lookbook generator — turns transparent cutouts + custom backdrops into IG-ready exports.
 *  • Method: "compose" (instant PIL overlay) or "ai" (Gemini Nano Banana re-photograph)
 *  • Formats: 1:1 square, 4:5 portrait, 9:16 story
 */
export default function LookbookGenerator({ itemId, photos }) {
  const [backgrounds, setBackgrounds] = useState([]);
  const [lookbooks, setLookbooks] = useState([]);
  const [sourcePath, setSourcePath] = useState("");
  const [bgId, setBgId] = useState("");
  const [method, setMethod] = useState("compose");
  const [format, setFormat] = useState("square");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [bgs, lbs] = await Promise.all([
      api.get("/backgrounds").then((r) => r.data).catch(() => []),
      api.get(`/inventory/${itemId}/lookbooks`).then((r) => r.data).catch(() => []),
    ]);
    setBackgrounds(bgs);
    setLookbooks(lbs);
  };

  useEffect(() => { if (itemId) load(); /* eslint-disable-next-line */ }, [itemId]);

  // Derive effective selections: explicit user choice falls back to the first available option.
  const effectiveSource = sourcePath || photos?.[0] || "";
  const effectiveBg = bgId || backgrounds[0]?.id || "";

  const generate = async () => {
    if (!effectiveSource) { toast.error("pick a source photo."); return; }
    if (!effectiveBg) { toast.error("pick a backdrop."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/inventory/${itemId}/lookbook`, {
        source_photo_path: effectiveSource,
        background_id: effectiveBg,
        method,
        format,
        caption,
      });
      setLookbooks((prev) => [data, ...prev]);
      toast.success(`lookbook generated · ${data.format}.`);
      setCaption("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "could not generate lookbook.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("remove this lookbook?")) return;
    try {
      await api.delete(`/lookbooks/${id}`);
      setLookbooks((prev) => prev.filter((l) => l.id !== id));
      toast.success("lookbook removed.");
    } catch {
      toast.error("could not remove.");
    }
  };

  const download = (lb) => {
    const a = document.createElement("a");
    a.href = fileUrl(lb.storage_path);
    a.download = `lookbook-${lb.format}-${lb.id.slice(0, 8)}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const copyCaption = async (lb) => {
    try {
      await navigator.clipboard.writeText(lb.caption || "");
      toast.success("caption copied.");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("clipboard copy failed", e);
      }
      toast.error("could not copy caption.");
    }
  };

  const noBackgrounds = backgrounds.length === 0;
  const noPhotos = !photos || photos.length === 0;

  return (
    <section className="bg-linen border border-taupe/50 p-6 sm:p-8 mb-10" data-testid="lookbook-generator">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl lowercase text-charcoal">lookbooks · instagram-ready</h2>
          <p className="text-xs text-espresso mt-1">drop a transparent cutout onto a backdrop. one click → catalogue-ready content.</p>
        </div>
        <button
          onClick={generate}
          disabled={busy || noPhotos || noBackgrounds}
          data-testid="lookbook-generate"
          className="bg-brass hover:bg-espresso text-ivory px-5 py-2.5 text-xs tracking-[0.25em] uppercase flex items-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
          {busy ? "composing…" : "generate lookbook"}
        </button>
      </div>

      {noPhotos && (
        <p className="text-xs text-red-700 italic mb-4">upload at least one photo to generate a lookbook.</p>
      )}
      {!noPhotos && noBackgrounds && (
        <p className="text-xs text-red-700 italic mb-4">no backdrops yet — open the image studio above and upload one in custom backdrop mode.</p>
      )}

      {/* Pickers */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div>
          <p className="editorial-label mb-2">source photo</p>
          <div className="grid grid-cols-4 gap-2" data-testid="lookbook-source-picker">
            {(photos || []).map((p) => (
              <button
                key={p}
                onClick={() => setSourcePath(p)}
                className={`aspect-square border ${sourcePath === p ? "border-brass ring-2 ring-brass/30" : "border-taupe/40"}`}
                data-testid={`lookbook-src-${p.slice(-12)}`}
              >
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="editorial-label mb-2">backdrop</p>
          <div className="grid grid-cols-4 gap-2" data-testid="lookbook-bg-picker">
            {backgrounds.map((b) => (
              <button
                key={b.id}
                onClick={() => setBgId(b.id)}
                className={`aspect-square border relative ${effectiveBg === b.id ? "border-brass ring-2 ring-brass/30" : "border-taupe/40"}`}
                data-testid={`lookbook-bg-${b.id.slice(0, 8)}`}
              >
                <img src={fileUrl(b.storage_path)} alt={b.name} className="w-full h-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-charcoal/85 text-ivory text-[0.55rem] tracking-wider px-1 py-0.5 truncate">{b.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div>
          <p className="editorial-label mb-2">method</p>
          <div className="flex flex-col gap-2 text-xs">
            <label className="flex items-center gap-2 text-charcoal">
              <input type="radio" name="lb-method" checked={method === "compose"} onChange={() => setMethod("compose")} data-testid="lb-method-compose" />
              <span>instant compose · pil overlay (free)</span>
            </label>
            <label className="flex items-center gap-2 text-charcoal">
              <input type="radio" name="lb-method" checked={method === "ai"} onChange={() => setMethod("ai")} data-testid="lb-method-ai" />
              <span>editorial re-photograph · gemini nano banana</span>
            </label>
          </div>
        </div>
        <div>
          <p className="editorial-label mb-2">format</p>
          <div className="flex flex-col gap-2 text-xs">
            {FORMATS.map((f) => (
              <label key={f.key} className="flex items-center gap-2 text-charcoal">
                <input type="radio" name="lb-format" checked={format === f.key} onChange={() => setFormat(f.key)} data-testid={`lb-format-${f.key}`} />
                <span>{f.label} <span className="text-taupe">· {f.w}×{f.h}</span></span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <p className="editorial-label mb-2">caption (optional)</p>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            data-testid="lookbook-caption"
            rows={4}
            placeholder="draft your IG caption here — saved with the lookbook."
            className="w-full bg-ivory border border-taupe/50 p-2 text-xs text-charcoal focus:outline-none focus:border-brass"
          />
        </div>
      </div>

      {/* Gallery */}
      {lookbooks.length > 0 && (
        <>
          <p className="editorial-label mb-3">generated lookbooks · {lookbooks.length}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="lookbook-gallery">
            {lookbooks.map((lb) => (
              <div key={lb.id} className="bg-ivory border border-taupe/40 group relative" data-testid={`lookbook-${lb.id.slice(0, 8)}`}>
                <div className="aspect-square overflow-hidden">
                  <img src={fileUrl(lb.storage_path)} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-3 space-y-1">
                  <p className="editorial-label">{lb.format} · {lb.width}×{lb.height}</p>
                  <p className="text-xs text-espresso">{lb.method === "ai" ? "editorial · ai" : "instant compose"}</p>
                  {lb.caption && <p className="text-xs text-charcoal italic mt-1 line-clamp-2">&ldquo;{lb.caption}&rdquo;</p>}
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => download(lb)} title="download" className="bg-ivory/95 p-1.5 border border-taupe hover:border-brass"><Download className="w-3 h-3 text-charcoal" /></button>
                  {lb.caption && <button onClick={() => copyCaption(lb)} title="copy caption" className="bg-ivory/95 p-1.5 border border-taupe hover:border-brass"><Copy className="w-3 h-3 text-charcoal" /></button>}
                  <button onClick={() => remove(lb.id)} title="remove" className="bg-ivory/95 p-1.5 border border-taupe hover:border-red-700"><Trash2 className="w-3 h-3 text-red-700" /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {lookbooks.length === 0 && !noPhotos && !noBackgrounds && (
        <div className="text-center py-10 border border-dashed border-taupe/50">
          <ImageIcon className="w-6 h-6 text-taupe mx-auto mb-2" />
          <p className="font-serif text-xl lowercase text-taupe">no lookbooks yet · generate your first.</p>
        </div>
      )}
    </section>
  );
}
